import React, { useState, useCallback, useRef } from "react";
import { X, Upload, FileSpreadsheet, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { importCSVFile, parseCSV } from "../../services/MailingListService";

/**
 * ImportCSVModal - Modal for importing contacts from CSV with column mapping
 */
const ImportCSVModal = ({ listId, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: ""
  });
  const [isImporting, setIsImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  // Parse CSV file for preview
  const parseCSVPreview = useCallback((selectedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const rows = parseCSV(text);

        if (rows.length < 2) {
          setError("CSV file must have at least a header row and one data row");
          return;
        }

        const headers = rows[0];
        const preview = rows.slice(1, 6); // First 5 data rows

        setCsvData({
          headers,
          preview,
          totalRows: rows.length - 1
        });

        // Auto-detect column mapping based on header names
        const autoMapping = { ...columnMapping };
        const headerLower = headers.map((h) => h.toLowerCase().trim());

        // Name detection
        const nameIdx = headerLower.findIndex(
          (h) => h === "name" || h === "full name" || h === "contact name" || h === "recipient"
        );
        if (nameIdx >= 0) autoMapping.name = headers[nameIdx];

        // Address line 1 detection
        const addr1Idx = headerLower.findIndex(
          (h) =>
            h === "address" ||
            h === "address1" ||
            h === "address_line1" ||
            h === "street" ||
            h === "street address"
        );
        if (addr1Idx >= 0) autoMapping.address_line1 = headers[addr1Idx];

        // Address line 2 detection
        const addr2Idx = headerLower.findIndex(
          (h) => h === "address2" || h === "address_line2" || h === "apt" || h === "unit" || h === "suite"
        );
        if (addr2Idx >= 0) autoMapping.address_line2 = headers[addr2Idx];

        // City detection
        const cityIdx = headerLower.findIndex((h) => h === "city" || h === "town");
        if (cityIdx >= 0) autoMapping.city = headers[cityIdx];

        // State detection
        const stateIdx = headerLower.findIndex(
          (h) => h === "state" || h === "province" || h === "region"
        );
        if (stateIdx >= 0) autoMapping.state = headers[stateIdx];

        // Zip detection
        const zipIdx = headerLower.findIndex(
          (h) => h === "zip" || h === "zipcode" || h === "zip_code" || h === "postal" || h === "postal_code"
        );
        if (zipIdx >= 0) autoMapping.zip = headers[zipIdx];

        // Phone detection
        const phoneIdx = headerLower.findIndex((h) => h.includes("phone") || h === "tel" || h === "mobile");
        if (phoneIdx >= 0) autoMapping.phone = headers[phoneIdx];

        // Email detection
        const emailIdx = headerLower.findIndex((h) => h.includes("email") || h === "e-mail");
        if (emailIdx >= 0) autoMapping.email = headers[emailIdx];

        setColumnMapping(autoMapping);
        setError(null);
      } catch (err) {
        setError("Failed to parse CSV file. Please ensure it's a valid CSV format.");
      }
    };
    reader.onerror = () => {
      setError("Failed to read file");
    };
    reader.readAsText(selectedFile);
  }, [columnMapping]);

  // Handle file selection
  const handleFileSelect = useCallback(
    (selectedFile) => {
      if (!selectedFile) return;

      // Validate file type
      if (!selectedFile.name.endsWith(".csv") && selectedFile.type !== "text/csv") {
        setError("Please select a CSV file");
        return;
      }

      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }

      setFile(selectedFile);
      setError(null);
      setResult(null);
      parseCSVPreview(selectedFile);
    },
    [parseCSVPreview]
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      handleFileSelect(droppedFile);
    },
    [handleFileSelect]
  );

  // Handle import
  const handleImport = async () => {
    if (!file || !csvData) return;

    // Validate required mappings
    if (!columnMapping.name) {
      setError("Please select a column for Contact Name");
      return;
    }
    if (!columnMapping.address_line1) {
      setError("Please select a column for Address Line 1");
      return;
    }
    if (!columnMapping.city) {
      setError("Please select a column for City");
      return;
    }
    if (!columnMapping.state) {
      setError("Please select a column for State");
      return;
    }
    if (!columnMapping.zip) {
      setError("Please select a column for ZIP Code");
      return;
    }

    try {
      setIsImporting(true);
      setError(null);
      const importResult = await importCSVFile(listId, file, columnMapping);
      setResult(importResult);

      // If successful, notify parent
      if (importResult.imported_count > 0) {
        setTimeout(() => {
          onImported();
        }, 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Render mapping select
  const MappingSelect = ({ label, field, required = false }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={columnMapping[field]}
        onChange={(e) => setColumnMapping((prev) => ({ ...prev, [field]: e.target.value }))}
        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
        disabled={isImporting}
      >
        <option value="">-- Select Column --</option>
        {csvData?.headers.map((header, idx) => (
          <option key={idx} value={header}>
            {header}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 dark:bg-slate-950/80 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold dark:text-white text-slate-900">
              Import Contacts from CSV
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isImporting}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Success result */}
          {result && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium mb-2">
                <CheckCircle className="w-5 h-5" />
                Import Complete
              </div>
              <p className="text-sm text-green-600 dark:text-green-400">
                Successfully imported {result.imported_count} contacts.
                {result.failed_count > 0 && ` ${result.failed_count} rows failed to import.`}
              </p>
            </div>
          )}

          {/* Dropzone - show if no file selected */}
          {!file && (
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isDragging
                  ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20"
                  : "border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload
                className={`w-10 h-10 mx-auto mb-3 ${
                  isDragging ? "text-sky-500" : "text-slate-400"
                }`}
              />
              <p className="text-slate-600 dark:text-slate-400 mb-2">
                Drag and drop a CSV file here, or{" "}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sky-600 hover:text-sky-500 font-medium"
                >
                  browse
                </button>
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500">
                Maximum file size: 10MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
              />
            </div>
          )}

          {/* File selected - show mapping and preview */}
          {file && csvData && !result && (
            <>
              {/* Selected file info */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium dark:text-white text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {csvData.totalRows.toLocaleString()} rows
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    setCsvData(null);
                    setColumnMapping({
                      name: "",
                      address_line1: "",
                      address_line2: "",
                      city: "",
                      state: "",
                      zip: "",
                      phone: "",
                      email: ""
                    });
                  }}
                  className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Change file
                </button>
              </div>

              {/* Column mapping */}
              <div>
                <h4 className="font-medium dark:text-white text-slate-900 mb-3">
                  Map CSV Columns
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Select which CSV column corresponds to each contact field.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <MappingSelect label="Contact Name" field="name" required />
                  <MappingSelect label="Address Line 1" field="address_line1" required />
                  <MappingSelect label="Address Line 2" field="address_line2" />
                  <MappingSelect label="City" field="city" required />
                  <MappingSelect label="State" field="state" required />
                  <MappingSelect label="ZIP Code" field="zip" required />
                  <MappingSelect label="Phone" field="phone" />
                  <MappingSelect label="Email" field="email" />
                </div>
              </div>

              {/* Preview */}
              <div>
                <h4 className="font-medium dark:text-white text-slate-900 mb-3">
                  Preview (first 5 rows)
                </h4>
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          {csvData.headers.map((header, idx) => (
                            <th
                              key={idx}
                              className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {csvData.preview.map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            {row.map((cell, cellIdx) => (
                              <td
                                key={cellIdx}
                                className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap max-w-[200px] truncate"
                              >
                                {cell || "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={onClose}
            disabled={isImporting}
            className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={handleImport}
              disabled={isImporting || !file || !csvData}
              className="flex-1 px-4 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-lg font-bold transition-colors shadow-lg shadow-sky-900/20 disabled:shadow-none flex items-center justify-center gap-2"
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                `Import ${csvData?.totalRows.toLocaleString() || 0} Contacts`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportCSVModal;
